import {
    commitRecoveredWalletForDevice,
    getClientShare,
    getLocalSelfCustodyPayloadStatus,
    isSelfCustodyReinitializationRequiredError,
    prepareRecoveredWalletForDevice,
    recoverLocalWalletTextWithShares,
    signWalletRecoveryProof,
    validateWalletTextForRecovery,
    walletRootIdentityPublicKey,
} from './bsv-signing'
import { assertCurrentUser, walletRequest, withSeedWordsFromServer } from './walletPasskeyRecovery'
import type { WalletRecoveryIdentity } from './walletPasskeyCrypto'

// Portable full-wallet backups (implementation plan B1). The saved file is the plain
// SHUAllet JSON (treechat_wallet_v1.json). Verification and restore re-read it locally,
// check both keys against the account identity, and prove both keys to the server;
// neither key is ever sent.

export const MAX_WALLET_BACKUP_BYTES = 65536

export interface WalletBackupStatus {
    identity: WalletRecoveryIdentity
    root_identity_key?: string | null
    verified_at: string | null
}

interface WalletBackupGrant {
    grant_id: string
    identity: WalletRecoveryIdentity
    proof_message: string
    root_identity_key?: string | null
}

export async function loadWalletBackupStatus(): Promise<WalletBackupStatus> {
    const userId = assertCurrentUser()
    const status = await walletRequest<WalletBackupStatus>('wallet_backup')
    if (status.identity.userId !== userId) throw new Error('Wallet backup account mismatch.')
    return status
}

export async function readWalletBackupFile(file: File): Promise<string> {
    if (file.size > MAX_WALLET_BACKUP_BYTES) {
        throw new Error('This file is too large to be a Treechat wallet backup.')
    }
    return file.text()
}

function assertBackupMatches(walletText: string, identity: WalletRecoveryIdentity) {
    try {
        validateWalletTextForRecovery(walletText, identity)
    } catch {
        throw new Error(
            'This backup is not for this Treechat wallet. Choose the backup saved from this account.',
        )
    }
}

async function backupGrant(
    purpose: 'verify' | 'restore',
    walletText: string,
    deviceId?: string,
): Promise<WalletBackupGrant> {
    const userId = assertCurrentUser()
    // Check the file locally before asking for a one-time server challenge.
    const status = await loadWalletBackupStatus()
    assertBackupMatches(walletText, status.identity)
    // A complete backup carries the seed words (plan B-R); restore still accepts an
    // older file because it restores the funded keys.
    if (
        purpose === 'verify' &&
        status.root_identity_key &&
        walletRootIdentityPublicKey(walletText) !== status.root_identity_key
    ) {
        throw new Error(
            "This backup was saved before your wallet's seed words were added. Save a new backup file and verify that one.",
        )
    }
    const grant = await walletRequest<WalletBackupGrant>('wallet_backup/options', {
        purpose,
        ...(deviceId ? { device_id: deviceId } : {}),
    })
    assertCurrentUser(userId)
    if (grant.identity.userId !== userId) throw new Error('Wallet backup account mismatch.')
    assertBackupMatches(walletText, grant.identity)
    return { ...grant, root_identity_key: status.root_identity_key }
}

async function preserveExistingRoot(walletText: string, grant: WalletBackupGrant): Promise<void> {
    if (!grant.root_identity_key || walletRootIdentityPublicKey(walletText)) return
    const userId = grant.identity.userId
    const local = await getClientShare(userId)
    if (!local?.clientShare || (await getLocalSelfCustodyPayloadStatus(userId)) !== 'compatible')
        return
    assertCurrentUser(userId)
    const server = await walletRequest<{ treechat_share: string }>(
        `server_share?device_id=${encodeURIComponent(local.deviceId)}`,
    )
    assertCurrentUser(userId)
    let currentWalletText: string
    try {
        currentWalletText = await recoverLocalWalletTextWithShares(
            userId,
            server.treechat_share,
            local.clientShare,
        )
    } catch (error) {
        // Older files can still repair a device whose local wallet cannot be read.
        if (isSelfCustodyReinitializationRequiredError(error)) return
        throw error
    }
    if (walletRootIdentityPublicKey(currentWalletText) === grant.root_identity_key) {
        throw new Error(
            "Restoring this older backup would remove this device's wallet seed words. Save a new backup file from this device and use that instead.",
        )
    }
}

/** Re-open a saved backup and record it as verified for this wallet generation. */
export async function verifyWalletBackupText(walletText: string): Promise<WalletBackupStatus> {
    const grant = await backupGrant('verify', walletText)
    return walletRequest<WalletBackupStatus>('wallet_backup/complete', {
        grant_id: grant.grant_id,
        proofs: signWalletRecoveryProof(walletText, grant.proof_message),
    })
}

/**
 * Restore the same wallet keys onto this browser as a fresh device. Local wallet
 * records change only after the server confirms the new device share.
 */
export async function restoreWalletFromBackupText(walletText: string): Promise<void> {
    const deviceId = crypto.randomUUID()
    const grant = await backupGrant('restore', walletText, deviceId)
    const userId = grant.identity.userId
    // An older file restores the funded keys; the device copy also gets the seed
    // words from Treechat's copy when the file predates them.
    const deviceWalletText = await withSeedWordsFromServer(walletText)
    const prepared = await prepareRecoveredWalletForDevice(
        userId,
        deviceId,
        deviceWalletText,
        grant.identity,
    )
    // Prepare captures the existing records first; a concurrent local change also
    // prevents the final commit from overwriting a wallet checked here.
    await preserveExistingRoot(deviceWalletText, grant)
    const shareHash = new Uint8Array(
        await crypto.subtle.digest(
            'SHA-256',
            new TextEncoder().encode(prepared.shares.treechatShare),
        ),
    )
    const shareHashHex = Array.from(shareHash, byte => byte.toString(16).padStart(2, '0')).join('')
    assertCurrentUser(userId)
    await walletRequest('wallet_backup/complete', {
        grant_id: grant.grant_id,
        proofs: signWalletRecoveryProof(
            walletText,
            `${grant.proof_message}\nServer share SHA256: ${shareHashHex}`,
        ),
        treechat_share: prepared.shares.treechatShare,
    })
    const registered = await walletRequest<{ treechat_share: string }>(
        `server_share?device_id=${encodeURIComponent(deviceId)}`,
    )
    if (registered.treechat_share !== prepared.shares.treechatShare) {
        throw new Error(
            'Device registration could not be verified. Your local wallet has not changed.',
        )
    }
    assertCurrentUser(userId)
    await commitRecoveredWalletForDevice(prepared)
}
